// Onboarding-request (1:1) editor — the 6th required intent record. 404-aware
// create/edit like PublicationEditor. Render-readiness is gated locally so a
// saved record can actually render: site_boundary_slug required + ≥1 trusted
// subnet, and the two HA auth modes are constrained to the infra-validator
// contract literals (defaulted single-option selects).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { onboardingRequest } from "../../api/endpoints/onboardingRequest";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import {
  HA_LOCAL_AUTH_MODES,
  HA_REMOTE_AUTH_MODES,
  REQUEST_STATUSES,
  type OnboardingRequestConfig,
  type OnboardingRequestIn,
  type RequestStatus,
} from "../../api/types/onboardingRequest";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { SelectField } from "../../components/forms/SelectField";
import { CheckboxField } from "../../components/forms/CheckboxField";
import { StringListField } from "../../components/forms/StringListField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { EmptyState } from "../../components/ui/EmptyState";

const DEFAULTS: OnboardingRequestIn = {
  requested_by: null,
  request_status: "proposed",
  site_boundary_slug: "",
  site_type: null,
  ha_instance_name: null,
  ha_local_url: null,
  ha_local_trusted_subnets: [],
  ha_remote_auth_mode: "freeipa_command_line",
  ha_local_auth_mode: "trusted_networks",
  ha_break_glass_required: false,
};

export function OnboardingRequestEditor({
  siteSlug,
  canWrite,
}: {
  siteSlug: string;
  canWrite: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    retry: false,
    queryKey: qk.onboardingRequest(siteSlug),
    queryFn: () => onboardingRequest.get(siteSlug).then((r) => r.data),
  });

  if (q.isPending)
    return <p style={{ color: "var(--text-dim)" }}>Loading onboarding request…</p>;
  const is404 = q.error instanceof ApiError && q.error.status === 404;
  if (q.error && !is404)
    return <ErrorBanner error={q.error} title="Failed to load onboarding request" />;

  if (q.data) {
    return <ExistingRequest siteSlug={siteSlug} canWrite={canWrite} data={q.data} />;
  }
  if (creating) {
    return (
      <RequestForm
        key={`new-${siteSlug}`}
        siteSlug={siteSlug}
        canWrite={canWrite}
        initial={DEFAULTS}
        onCancel={() => setCreating(false)}
      />
    );
  }
  return (
    <EmptyState
      title="No onboarding request"
      description="The site's greenfield onboarding intent (boundary slug, HA local access) — the 6th record needed to render request.yml."
      action={
        canWrite ? (
          <button type="button" style={primaryBtn} onClick={() => setCreating(true)}>
            Create onboarding request
          </button>
        ) : null
      }
    />
  );
}

function ExistingRequest({
  siteSlug,
  canWrite,
  data,
}: {
  siteSlug: string;
  canWrite: boolean;
  data: OnboardingRequestConfig;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <RequestForm
        key={`existing-${siteSlug}`}
        siteSlug={siteSlug}
        canWrite={canWrite}
        initial={toIn(data)}
        existing
        onCancel={() => setEditing(false)}
      />
    );
  }
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ fontSize: "0.9rem" }}>
        <code>{data.request_id}</code> · status <strong>{data.request_status}</strong> ·
        boundary <code>{data.site_boundary_slug ?? "—"}</code>
      </div>
      {canWrite && (
        <div>
          <button type="button" style={primaryBtn} onClick={() => setEditing(true)}>
            Edit request intent
          </button>
        </div>
      )}
    </div>
  );
}

interface ReqDraft {
  requested_by: string;
  request_status: RequestStatus;
  site_boundary_slug: string;
  site_type: string;
  ha_instance_name: string;
  ha_local_url: string;
  ha_local_trusted_subnets: string[];
  ha_remote_auth_mode: string;
  ha_local_auth_mode: string;
  ha_break_glass_required: boolean;
}

function RequestForm({
  siteSlug,
  canWrite,
  initial,
  existing,
  onCancel,
}: {
  siteSlug: string;
  canWrite: boolean;
  initial: OnboardingRequestIn;
  existing?: boolean;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ReqDraft>(() => toDraft(initial));
  const set = <K extends keyof ReqDraft>(k: K, v: ReqDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: (body: OnboardingRequestIn) =>
      existing
        ? onboardingRequest.update(siteSlug, body)
        : onboardingRequest.create(siteSlug, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.onboardingRequest(siteSlug) });
      onCancel?.();
    },
  });
  const del = useMutation({
    mutationFn: () => onboardingRequest.remove(siteSlug),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.onboardingRequest(siteSlug) }),
  });

  const isValid =
    draft.site_boundary_slug.trim() !== "" &&
    draft.ha_local_trusted_subnets.length >= 1 &&
    isAllowed(draft.ha_remote_auth_mode, HA_REMOTE_AUTH_MODES) &&
    isAllowed(draft.ha_local_auth_mode, HA_LOCAL_AUTH_MODES);
  const pending = save.isPending || del.isPending;
  const disabled = !canWrite || pending;

  return (
    <FormShell
      submitLabel={existing ? "Save request" : "Create onboarding request"}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid || disabled) return;
        save.mutate(fromDraft(draft));
      }}
      onCancel={onCancel}
      pending={pending}
      submitDisabled={!canWrite || !isValid}
      error={save.error ?? del.error}
      errorTitle="Onboarding request save failed"
      footer={
        existing && canWrite ? (
          <div>
            <button
              type="button"
              style={dangerBtn}
              disabled={pending}
              onClick={() => {
                if (window.confirm(`Delete onboarding request for ${siteSlug}?`)) del.mutate();
              }}
            >
              Delete onboarding request
            </button>
          </div>
        ) : undefined
      }
    >
      <SelectField
        label="Request status"
        value={draft.request_status}
        onChange={(v) => set("request_status", v as RequestStatus)}
        options={REQUEST_STATUSES}
        disabled={disabled}
      />
      <TextField
        label="Site boundary slug"
        value={draft.site_boundary_slug}
        onChange={(v) => set("site_boundary_slug", v)}
        required
        placeholder="126-a-bankers-avenue"
        helper="NetBird/site boundary identifier — drives the rendered netbird groups."
        disabled={disabled}
      />
      <TextField
        label="Site type"
        value={draft.site_type}
        onChange={(v) => set("site_type", v)}
        placeholder="residential"
        disabled={disabled}
      />
      <TextField
        label="HA instance name"
        value={draft.ha_instance_name}
        onChange={(v) => set("ha_instance_name", v)}
        placeholder="fatima-ha"
        disabled={disabled}
      />
      <TextField
        label="HA local URL"
        value={draft.ha_local_url}
        onChange={(v) => set("ha_local_url", v)}
        placeholder="https://homeassistant.local:8123"
        disabled={disabled}
      />
      <StringListField
        label="HA local trusted subnets"
        value={draft.ha_local_trusted_subnets}
        onChange={(v) => set("ha_local_trusted_subnets", v)}
        placeholder={"10.129.0.0/24"}
        helper="One CIDR per line — at least one required (gates render-readiness)."
        disabled={disabled}
      />
      <SelectField
        label="HA remote auth mode"
        value={draft.ha_remote_auth_mode}
        onChange={(v) => set("ha_remote_auth_mode", v)}
        options={HA_REMOTE_AUTH_MODES}
        helper="Fixed by the infra apply-time validator."
        disabled={disabled}
      />
      <SelectField
        label="HA local auth mode"
        value={draft.ha_local_auth_mode}
        onChange={(v) => set("ha_local_auth_mode", v)}
        options={HA_LOCAL_AUTH_MODES}
        helper="Fixed by the infra apply-time validator."
        disabled={disabled}
      />
      <CheckboxField
        label="Break-glass required"
        checked={draft.ha_break_glass_required}
        onChange={(v) => set("ha_break_glass_required", v)}
        disabled={disabled}
      />
    </FormShell>
  );
}

function toIn(c: OnboardingRequestConfig): OnboardingRequestIn {
  const { site_id: _s, request_id: _r, requested_at: _a, ...rest } = c;
  return { ...rest, request_status: rest.request_status as RequestStatus };
}

// Whether a stored value is one of the selectable contract literals.
function isAllowed(
  value: string,
  options: ReadonlyArray<{ value: string }>,
): boolean {
  return options.some((o) => o.value === value);
}

function toDraft(p: OnboardingRequestIn): ReqDraft {
  // Coerce any legacy/manual auth-mode value that isn't a contract literal back
  // to the canonical default, so an existing invalid record can't be re-saved
  // (the infra apply-time validator requires the exact literals).
  const remote = isAllowed(p.ha_remote_auth_mode, HA_REMOTE_AUTH_MODES)
    ? p.ha_remote_auth_mode
    : "freeipa_command_line";
  const local = isAllowed(p.ha_local_auth_mode, HA_LOCAL_AUTH_MODES)
    ? p.ha_local_auth_mode
    : "trusted_networks";
  return {
    requested_by: p.requested_by ?? "",
    request_status: p.request_status ?? "proposed",
    site_boundary_slug: p.site_boundary_slug ?? "",
    site_type: p.site_type ?? "",
    ha_instance_name: p.ha_instance_name ?? "",
    ha_local_url: p.ha_local_url ?? "",
    ha_local_trusted_subnets: p.ha_local_trusted_subnets ?? [],
    ha_remote_auth_mode: remote,
    ha_local_auth_mode: local,
    ha_break_glass_required: p.ha_break_glass_required ?? false,
  };
}

function fromDraft(d: ReqDraft): OnboardingRequestIn {
  const orNull = (s: string) => (s.trim() === "" ? null : s.trim());
  return {
    requested_by: orNull(d.requested_by),
    request_status: d.request_status,
    site_boundary_slug: orNull(d.site_boundary_slug),
    site_type: orNull(d.site_type),
    ha_instance_name: orNull(d.ha_instance_name),
    ha_local_url: orNull(d.ha_local_url),
    ha_local_trusted_subnets: d.ha_local_trusted_subnets,
    ha_remote_auth_mode: d.ha_remote_auth_mode,
    ha_local_auth_mode: d.ha_local_auth_mode,
    ha_break_glass_required: d.ha_break_glass_required,
  };
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none", padding: "8px 16px",
  borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
const dangerBtn: React.CSSProperties = {
  background: "transparent", color: "var(--crit)", border: "1px solid var(--crit)",
  padding: "6px 12px", borderRadius: 4, fontSize: "0.85rem", cursor: "pointer",
};
